import React, { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { developerAccessTokenState } from "../global-state";
import "react-toastify/dist/ReactToastify.css";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import { DataGrid } from "@mui/x-data-grid";
import LoadingSpinner from "../common/loading";
import { SAND_CAT } from "../config";
import { useToast } from "../common/toast";
import { useTheme } from "@mui/material/styles";
import CircularProgress from "@mui/material/CircularProgress";

const WaitlistUsersView = () => {
  const [developerAccessToken, setDeveloperAccessToken] = useRecoilState(
    developerAccessTokenState,
  );
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const { successToast, errorToast } = useToast();
  const [spinButton, setSpinButton] = useState(null);
  const theme = useTheme();

  // State for selected rows
  const [selectedRows, setSelectedRows] = useState([]);

  useEffect(() => {
    loadWaitlistUsers();
  }, []);

  const loadWaitlistUsers = async () => {
    setLoading(true);
    const statusUrl = `${SAND_CAT}/waitlist/list`;
    try {
      const response = await fetch(statusUrl, {
        method: "GET",
        headers: {
          AdminAuthorization: `Bearer ${developerAccessToken}`,
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data);
      }
      console.log(data);

      // Add unique id and transform timestamp
      const usersWithId = data
        .map((user, index) => ({
          ...user,
          id: index + 1,
          formattedTimestamp: new Date(user.timestamp).toLocaleString(),
        }))
        // Sort by timestamp (earliest first)
        .sort((a, b) => a.timestamp - b.timestamp);

      setUsers(usersWithId);
      setLoading(false);
    } catch (error) {
      errorToast("Error fetching waitlist users! Please retry.");
      console.log(error);
      setLoading(false);
    }
  };

  // Columns for the DataGrid
  const columns = [
    {
      field: "id",
      headerName: "ID",
      width: 10,
      hide: true,
    },
    {
      field: "userEmail",
      headerName: "Email",
      width: 300,
      sortable: true,
    },
    {
      field: "formattedTimestamp",
      headerName: "Joined At",
      width: 250,
      sortable: true,
    },
    {
      field: "wait",
      headerName: "Wait Status",
      width: 100,
      renderCell: params => (params.value ? "Waiting" : "Processed"),
    },
    {
      field: "enroll",
      headerName: "Enrollment Status",
      width: 150,
      renderCell: params => (params.value ? "Enrolled" : "Not Enrolled"),
    },
    {
      field: "gsi",
      headerName: "GSI",
      width: 100,
    },
  ];

  // Handle enrolling selected users
  const handleEnrollUsers = async () => {
    setSpinButton("enroll");

    // Get the actual user emails based on selected row indices
    const selectedEmails = selectedRows
      .map(rowId => {
        const user = users.find(u => u.id === rowId);
        return user ? user.userEmail : null;
      })
      .filter(email => email !== null);

    try {
      const response = await fetch(`${SAND_CAT}/waitlist/enroll`, {
        method: "POST",
        headers: {
          AdminAuthorization: `Bearer ${developerAccessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ emails: selectedEmails }),
      });

      const jsonData = await response.json();

      if (!response.ok) {
        throw new Error(jsonData);
      }

      // Check for partial success
      if (jsonData.success) {
        successToast(`Successfully enrolled`);

        // Refresh the list
        loadWaitlistUsers();
      } else {
        errorToast("Error!");
      }
    } catch (error) {
      errorToast("Error enrolling users. Please retry.");
      console.error("Error enrolling users:", error);
    }

    setSpinButton(null);
  };

  // Handle selection change
  const handleSelectionChange = newSelection => {
    setSelectedRows(newSelection);
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <Grid container spacing={2} direction="column">
          <Grid container spacing={2}>
            <Box pl={4} pt={4}>
              <Button
                variant="contained"
                onClick={handleEnrollUsers}
                disabled={spinButton !== null || selectedRows.length === 0}
              >
                Enroll Selected Users
                {spinButton === "enroll" && (
                  <CircularProgress
                    size={20}
                    style={{
                      marginLeft: 10,
                      color: theme.palette.background.l0,
                    }}
                  />
                )}
              </Button>
            </Box>
          </Grid>
          <Grid item xs={10}>
            <Box sx={{ height: "100%", width: "100%" }}>
              <DataGrid
                rows={users}
                columns={columns}
                initialState={{
                  pagination: {
                    paginationModel: {
                      pageSize: 20,
                    },
                  },
                }}
                pageSizeOptions={[20]}
                checkboxSelection
                onRowSelectionModelChange={handleSelectionChange}
                sx={{
                  "& .MuiDataGrid-root": {
                    backgroundColor: theme.palette.background.dataGrid,
                  },
                  "& .MuiDataGrid-cell": {
                    backgroundColor: theme.palette.background.dataGrid,
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: theme.palette.background.dataGrid,
                  },
                }}
              />
            </Box>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default WaitlistUsersView;
